import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import AppIcon from '../common/AppIcon';

interface PlannerData {
  name: string;
  color: string | null;
}

interface PlannersStepProps {
  planners: PlannerData[];
  onChange: (planners: PlannerData[]) => void;
  onPendingNameChange?: (name: string) => void;
  addPendingRef?: React.MutableRefObject<(() => void) | null>;
}

const COLOR_OPTIONS = [
  '#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E',
  '#E57373', '#9575CD', '#4FC3F7', '#81C784', '#FF8A65',
  '#A1887F', '#90A4AE',
];

const PlannersStep: React.FC<PlannersStepProps> = ({ planners, onChange, onPendingNameChange, addPendingRef }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);

  // Notify parent of pending name changes
  React.useEffect(() => {
    onPendingNameChange?.(name);
  }, [name, onPendingNameChange]);

  const handleAdd = () => {
    if (!name.trim()) return;
    onChange([...planners, { name: name.trim(), color }]);
    setName('');
    setColor(null);
  };

  // Expose handleAdd to parent via ref
  React.useEffect(() => {
    if (addPendingRef) {
      addPendingRef.current = handleAdd;
    }
  });

  const handleRemove = (index: number) => {
    onChange(planners.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography
        variant="h6"
        sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600, mb: 1 }}
      >
        Who's helping plan?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add the people involved in planning your wedding. You can always add, remove, or change planners later.
      </Typography>

      {/* Added planners */}
      {planners.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {planners.map((p, i) => (
            <Chip
              key={i}
              label={p.name}
              onDelete={() => handleRemove(i)}
              sx={{
                bgcolor: p.color ? `${p.color}25` : 'grey.100',
                color: p.color || 'grey.800',
                fontWeight: 500,
                border: `1px solid ${p.color || '#E4DED8'}`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Add planner form */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label="Planner Name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          disabled={!name.trim()}
          sx={{ minWidth: 'auto', px: 2, height: 40 }}
        >
          <AppIcon name="add" />
        </Button>
      </Box>

      {/* Color picker */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Color (optional)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {COLOR_OPTIONS.map((c) => (
            <Box
              key={c}
              onClick={() => setColor(color === c ? null : c)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: c,
                cursor: 'pointer',
                border: color === c ? '3px solid' : '2px solid transparent',
                borderColor: color === c ? 'grey.800' : 'transparent',
                outline: color === c ? '2px solid white' : 'none',
                outlineOffset: -3,
                transition: 'transform 0.15s ease',
                '&:hover': { opacity: 0.8, transform: 'scale(1.15)' },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PlannersStep;
